import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  Chip,
  Autocomplete,
} from "@mui/material";

interface CronScheduleBuilderProps {
  value: string;
  onChange: (cronExpression: string) => void;
  error?: string;
  showValidation?: boolean; // Only show validation errors when true
  readonly?: boolean; // Disable inputs when true
}

type FrequencyType = "hourly" | "daily" | "weekly" | "monthly" | "custom";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];
const DAYS_OF_WEEK = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * Parse a cron expression and extract frequency, hour, minute, etc.
 * Returns null if it doesn't match common patterns
 */
const parseCronExpression = (cron: string): {
  frequency: FrequencyType;
  hour: number;
  minute: number;
  dayOfWeek?: string;
  dayOfMonth?: number;
} | null => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Check if it's a complex pattern (*/n, ranges, lists, etc.)
  const isComplexPattern = (field: string) => {
    return field.includes('/') || field.includes('-') || field.includes(',');
  };

  // If minute or hour has complex patterns, treat as custom
  if (isComplexPattern(minute) || isComplexPattern(hour)) {
    return {
      frequency: "custom",
      hour: 9,
      minute: 0,
    };
  }

  // Hourly: "0 * * * *" or "15 * * * *"
  if (hour === "*" && dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    const parsedMinute = parseInt(minute, 10);
    if (isNaN(parsedMinute)) {
      return { frequency: "custom", hour: 9, minute: 0 };
    }
    return {
      frequency: "hourly",
      hour: 0, // Not used for hourly
      minute: parsedMinute,
    };
  }

  // Daily: "0 9 * * *"
  if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    const parsedHour = parseInt(hour, 10);
    const parsedMinute = parseInt(minute, 10);
    if (isNaN(parsedHour) || isNaN(parsedMinute)) {
      return { frequency: "custom", hour: 9, minute: 0 };
    }
    return {
      frequency: "daily",
      hour: parsedHour,
      minute: parsedMinute,
    };
  }

  // Weekly: "0 9 * * 1" (Monday)
  if (dayOfMonth === "*" && month === "*" && dayOfWeek !== "*") {
    const parsedHour = parseInt(hour, 10);
    const parsedMinute = parseInt(minute, 10);
    if (isNaN(parsedHour) || isNaN(parsedMinute)) {
      return { frequency: "custom", hour: 9, minute: 0 };
    }
    return {
      frequency: "weekly",
      hour: parsedHour,
      minute: parsedMinute,
      dayOfWeek,
    };
  }

  // Monthly: "0 9 1 * *" (1st of month)
  if (dayOfMonth !== "*" && month === "*" && dayOfWeek === "*") {
    const parsedHour = parseInt(hour, 10);
    const parsedMinute = parseInt(minute, 10);
    const parsedDay = parseInt(dayOfMonth, 10);
    if (isNaN(parsedHour) || isNaN(parsedMinute) || isNaN(parsedDay)) {
      return { frequency: "custom", hour: 9, minute: 0 };
    }
    return {
      frequency: "monthly",
      hour: parsedHour,
      minute: parsedMinute,
      dayOfMonth: parsedDay,
    };
  }

  // Custom/other patterns
  return {
    frequency: "custom",
    hour: 9,
    minute: 0,
  };
};

/**
 * Build a cron expression from user-friendly inputs
 */
const buildCronExpression = (
  frequency: FrequencyType,
  hour: number,
  minute: number,
  dayOfWeek?: string,
  dayOfMonth?: number
): string => {
  switch (frequency) {
    case "hourly":
      return `${minute} * * * *`;
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekly":
      return `${minute} ${hour} * * ${dayOfWeek || "1"}`;
    case "monthly":
      return `${minute} ${hour} ${dayOfMonth || "1"} * *`;
    default:
      return `${minute} ${hour} * * *`;
  }
};

/**
 * Validate cron expression format
 */
export const validateCronExpression = (cron: string): { valid: boolean; error?: string } => {
  const parts = cron.trim().split(/\s+/);
  
  if (parts.length !== 5) {
    return { valid: false, error: "Cron expression must have exactly 5 parts (minute hour day month weekday)" };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Helper to check if a field is valid (allows *, numbers, ranges, steps, lists)
  const isValidCronField = (field: string, min: number, max: number): boolean => {
    const trimmed = field.trim();
    if (!trimmed) return false;
    
    // Allow simple wildcard
    if (trimmed === "*") return true;
    
    // Support lists separated by commas
    const listParts = trimmed.split(",");
    return listParts.every((part) => {
      const token = part.trim();
      if (!token) {
        // Disallow empty tokens, e.g. ",15" or "1,,2"
        return false;
      }
      
      // Handle step syntax: base/step
      const stepSplit = token.split("/");
      if (stepSplit.length > 2) {
        return false;
      }
      const rangePart = stepSplit[0];
      const stepPart = stepSplit[1];
      let step: number | null = null;
      if (stepPart !== undefined) {
        if (!/^\d+$/.test(stepPart)) {
          return false;
        }
        step = parseInt(stepPart, 10);
        if (step <= 0) {
          // Step must be a positive integer
          return false;
        }
      }
      
      // Handle wildcard with step: "*/n"
      if (rangePart === "*") {
        // "*" alone is handled above; here we only accept "*/n"
        return step !== null;
      }
      
      // Range "a-b" or "a-b/n"
      if (rangePart.includes("-")) {
        const [startStr, endStr] = rangePart.split("-");
        if (!startStr || !endStr) {
          // Disallow "1-" or "-5"
          return false;
        }
        if (!/^\d+$/.test(startStr) || !/^\d+$/.test(endStr)) {
          return false;
        }
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);
        if (start >= end) {
          // Reject reversed ranges (10-5) and equal ranges (10-10)
          // Equal ranges like "10-10" are not valid - no values in range
          return false;
        }
        if (start < min || end > max) {
          return false;
        }
        // Step (if present) already validated as positive integer
        return true;
      }
      
      // Single numeric value "a" or "a/n" (though "a/n" is uncommon, croniter allows it)
      if (!/^\d+$/.test(rangePart)) {
        return false;
      }
      const value = parseInt(rangePart, 10);
      if (value < min || value > max) {
        return false;
      }
      // Step, if present, already validated as positive integer
      return true;
    });
  };

  // Validate each field
  if (!isValidCronField(minute, 0, 59)) {
    return { valid: false, error: "Minute must be 0-59, *, or a valid pattern (e.g., */15, 0-30, 0,15,30)" };
  }

  if (!isValidCronField(hour, 0, 23)) {
    return { valid: false, error: "Hour must be 0-23, *, or a valid pattern (e.g., */2, 9-17, 9,12,15)" };
  }

  if (!isValidCronField(dayOfMonth, 1, 31)) {
    return { valid: false, error: "Day of month must be 1-31, *, or a valid pattern" };
  }

  if (!isValidCronField(month, 1, 12)) {
    return { valid: false, error: "Month must be 1-12, *, or a valid pattern" };
  }

  if (!isValidCronField(dayOfWeek, 0, 7)) {
    return { valid: false, error: "Day of week must be 0-7, *, or a valid pattern" };
  }

  return { valid: true };
};

/**
 * Generate human-readable description from cron expression
 */
export const describeCronExpression = (cron: string): string => {
  const parsed = parseCronExpression(cron);
  if (!parsed) return cron;

  // If parsing resulted in NaN values, it's a complex pattern - return raw cron
  if (isNaN(parsed.hour) || isNaN(parsed.minute)) {
    return cron;
  }

  const hourStr = parsed.hour.toString().padStart(2, "0");
  const minuteStr = parsed.minute.toString().padStart(2, "0");
  const timeStr = `${hourStr}:${minuteStr}`;

  switch (parsed.frequency) {
    case "hourly":
      return `Every hour at :${minuteStr}`;
    case "daily":
      return `Every day at ${timeStr}`;
    case "weekly": {
      const day = DAYS_OF_WEEK.find((d) => d.value === parsed.dayOfWeek);
      return `Every ${day?.label || "week"} at ${timeStr}`;
    }
    case "monthly":
      return `On day ${parsed.dayOfMonth} of every month at ${timeStr}`;
    default:
      return cron;
  }
};

/**
 * Generate a schedule label (e.g., "Hourly Schedule", "Daily Schedule") from cron expression
 */
export const getScheduleLabel = (cron: string): string => {
  const parsed = parseCronExpression(cron);
  if (!parsed) return "Custom Schedule";

  switch (parsed.frequency) {
    case "hourly":
      return "Hourly Schedule";
    case "daily":
      return "Daily Schedule";
    case "weekly":
      return "Weekly Schedule";
    case "monthly":
      return "Monthly Schedule";
    case "custom":
      return "Custom Schedule";
    default:
      return "Daily Schedule";
  }
};

/**
 * Reusable Minute Autocomplete Component
 * Provides a dropdown with common minute values (0, 15, 30, 45) and allows free text input (0-59)
 */
interface MinuteAutocompleteProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  label?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  sx?: any;
}

const MinuteAutocomplete: React.FC<MinuteAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  label = "Minute",
  size = "small",
  fullWidth = true,
  sx = {},
}) => {
  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      value={value.toString()}
      options={MINUTES.map(m => m.toString())}
      getOptionLabel={(option) => {
        const num = typeof option === 'string' ? parseInt(option, 10) : option;
        return `:${num.toString().padStart(2, "0")}`;
      }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          sx={{ fontSize: "12px" }}
        />
      )}
      onChange={(_, newValue) => {
        if (newValue !== null) {
          const cleanValue = String(newValue).replace(/^:/, '');
          const numValue = parseInt(cleanValue, 10);
          if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
            onChange(numValue);
          }
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        // Don't update on reset - this prevents the useEffect loop
        if (reason === 'reset') return;
        
        if (newInputValue !== null && newInputValue !== '') {
          const cleanValue = newInputValue.replace(/^:/, '');
          const numValue = parseInt(cleanValue, 10);
          if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
            onChange(numValue);
          }
        }
      }}
      sx={{ fontSize: "12px", ...sx }}
    />
  );
};

export const CronScheduleBuilder: React.FC<CronScheduleBuilderProps> = ({
  value,
  onChange,
  error,
  showValidation = false, // Default to false - only validate on submit
  readonly = false,
}) => {
  const [frequency, setFrequency] = useState<FrequencyType>("daily");
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [dayOfWeek, setDayOfWeek] = useState("1"); // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [customCron, setCustomCron] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastParsedValue, setLastParsedValue] = useState("");

  // Initialize from value prop and re-sync when value changes externally
  useEffect(() => {
    // Only parse if value has actually changed from what we last parsed
    if (value && value !== lastParsedValue) {
      const parsed = parseCronExpression(value);
      if (parsed) {
        setFrequency(parsed.frequency);
        setHour(parsed.hour);
        setMinute(parsed.minute);
        if (parsed.dayOfWeek) setDayOfWeek(parsed.dayOfWeek);
        if (parsed.dayOfMonth) setDayOfMonth(parsed.dayOfMonth);
        if (parsed.frequency === "custom") setCustomCron(value);
        setLastParsedValue(value);
      }
      setIsInitialized(true);
    }
  }, [value, lastParsedValue]);

  // Update parent when values change (but only after initialization)
  useEffect(() => {
    if (!isInitialized) return;
    
    const newCron = frequency === "custom" 
      ? customCron 
      : buildCronExpression(frequency, hour, minute, dayOfWeek, dayOfMonth);
    
    // Only call onChange if value actually changed
    if (newCron !== value) {
      onChange(newCron);
      setLastParsedValue(newCron); // Track what we sent to parent
    }
  }, [frequency, hour, minute, dayOfWeek, dayOfMonth, customCron, isInitialized, onChange, value]);

  const validation = validateCronExpression(frequency === "custom" ? customCron : buildCronExpression(frequency, hour, minute, dayOfWeek, dayOfMonth));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Frequency Selector */}
      <FormControl fullWidth size="small">
        <InputLabel sx={{ fontSize: "11px", fontWeight: 700 }}>Frequency</InputLabel>
        <Select
          value={frequency}
          label="Frequency"
          disabled={readonly}
          onChange={(e) => setFrequency(e.target.value as FrequencyType)}
          sx={{ fontSize: "12px" }}
        >
          <MenuItem value="hourly" sx={{ fontSize: "12px" }}>Hourly</MenuItem>
          <MenuItem value="daily" sx={{ fontSize: "12px" }}>Daily</MenuItem>
          <MenuItem value="weekly" sx={{ fontSize: "12px" }}>Weekly</MenuItem>
          <MenuItem value="monthly" sx={{ fontSize: "12px" }}>Monthly</MenuItem>
          <MenuItem value="custom" sx={{ fontSize: "12px" }}>Custom (Advanced)</MenuItem>
        </Select>
      </FormControl>

      {frequency !== "custom" ? (
        <>
          {/* Time Selection */}
          {frequency === "hourly" ? (
            /* Hourly: Only show minute selector */
            <MinuteAutocomplete
              value={minute}
              onChange={setMinute}
              disabled={readonly}
              label="Minute (past the hour)"
              size="small"
              fullWidth={true}
            />
          ) : (
            /* Daily/Weekly/Monthly: Show hour and minute */
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: "11px", fontWeight: 700 }}>Hour</InputLabel>
                <Select
                  value={hour}
                  label="Hour"
                  disabled={readonly}
                  onChange={(e) => setHour(Number(e.target.value))}
                  sx={{ fontSize: "12px" }}
                >
                  {HOURS.map((h) => (
                    <MenuItem key={h} value={h} sx={{ fontSize: "12px" }}>
                      {h.toString().padStart(2, "0")}:00
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <MinuteAutocomplete
                value={minute}
                onChange={setMinute}
                disabled={readonly}
                label="Minute"
                size="small"
                fullWidth={true}
                sx={{ "& .MuiInputLabel-root": { fontSize: "11px", fontWeight: 700 } }}
              />
            </Box>
          )}

          {/* Day Selection (Weekly) */}
          {frequency === "weekly" && (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "11px", fontWeight: 700 }}>Day of Week</InputLabel>
              <Select
                value={dayOfWeek}
                label="Day of Week"
                disabled={readonly}
                onChange={(e) => setDayOfWeek(e.target.value)}
                sx={{ fontSize: "12px" }}
              >
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day.value} value={day.value} sx={{ fontSize: "12px" }}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Day Selection (Monthly) */}
          {frequency === "monthly" && (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "11px", fontWeight: 700 }}>Day of Month</InputLabel>
              <Select
                value={dayOfMonth}
                label="Day of Month"
                disabled={readonly}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
                sx={{ fontSize: "12px" }}
              >
                {DAYS_OF_MONTH.map((day) => (
                  <MenuItem key={day} value={day} sx={{ fontSize: "12px" }}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      ) : (
        /* Custom Cron Input */
        <TextField
          fullWidth
          size="small"
          label="Cron Expression"
          value={customCron}
          disabled={readonly}
          onChange={(e) => setCustomCron(e.target.value)}
          placeholder="0 9 * * *"
          helperText="Format: minute hour day month weekday"
          error={showValidation && (!!error || !validation.valid)}
          InputLabelProps={{ sx: { fontSize: "11px", fontWeight: 700 } }}
          inputProps={{ style: { fontSize: "12px" } }}
          FormHelperTextProps={{ sx: { fontSize: "10px", mt: 0.5 } }}
        />
      )}

      {/* Preview */}
      <Box sx={{ p: 1.5, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0" }}>
        <Typography variant="caption" sx={{ color: "#64748B", fontSize: "10px", fontWeight: 700, display: "block", mb: 0.5 }}>
          Schedule Preview
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip 
            label={describeCronExpression(frequency === "custom" ? customCron : buildCronExpression(frequency, hour, minute, dayOfWeek, dayOfMonth))}
            size="small"
            sx={{ fontSize: "11px", fontWeight: 600 }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "10px", display: "block", mt: 0.5, fontFamily: "monospace" }}>
          {frequency === "custom" ? customCron : buildCronExpression(frequency, hour, minute, dayOfWeek, dayOfMonth)}
        </Typography>
      </Box>

      {/* Validation Error - Only show when validation is requested */}
      {showValidation && (error || !validation.valid) && (
        <Alert severity="error" sx={{ fontSize: "11px", py: 0.5 }}>
          {error || validation.error}
        </Alert>
      )}
    </Box>
  );
};
